import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ArrowDownTrayIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline";

import useOffers from "../../data/useOffers";
import usePartMutations from "../../data/usePartMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IPart } from "../../models/Part";
import classNames from "../../utils/classNames";
import AiTitleGenerator from "./AiTitleGenerator";
import { computeEbayListingTitle } from "./EbayListingTitle";

export default function WilmaTitleRow({ part }: { part: IPart }) {
  const { update } = useOffers();
  const { patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Title");
  const queryClient = useQueryClient();

  const [previousTitle, setPreviousTitle] = useState<string | null>(null);

  const offer = part.offers?.[0];
  const wilmaTitle = (computeEbayListingTitle(part, null, true) ?? "").trim();
  const currentTitle = (offer?.title ?? "").trim();
  const titleUpdated = !!currentTitle && !!wilmaTitle && currentTitle !== wilmaTitle;
  const canEdit = !!offer && part.soldDate == null;

  const applyTitleToCache = (title: string) => {
    queryClient.getQueriesData(["pagedTable"]).forEach(([key]) => {
      queryClient.setQueryData(key, (current: any) => {
        if (!current?.data?.rows) return current;
        return {
          ...current,
          data: {
            ...current.data,
            rows: current.data.rows.map((row: any) =>
              row.id === part.id && row.offers?.[0]
                ? { ...row, offers: [{ ...row.offers[0], title }, ...row.offers.slice(1)] }
                : row
            ),
          },
        };
      });
    });
  };

  const saveTitle = (title: string, recordUndo: boolean) => {
    const trimmed = title.trim();
    if (!offer || !trimmed || part.soldDate != null) return;

    const prior = offer.title ?? "";
    const updatedOffer = { ...offer, title: trimmed };

    update.mutate(updatedOffer, {
      ...saveHandlers,
      onSuccess: () => {
        saveHandlers.onSuccess();
        offer.title = trimmed;
        applyTitleToCache(trimmed);
        if (recordUndo) setPreviousTitle(prior.trim() ? prior : null);
        if (!part.titleConfigured) {
          patchPart.mutate(
            { id: part.id, titleConfigured: true },
            { onSuccess: () => (part.titleConfigured = true) }
          );
        }
      },
    });
  };

  const handleApply = () => saveTitle(wilmaTitle, true);

  const handleUndo = () => {
    if (!previousTitle) return;
    saveTitle(previousTitle, false);
    setPreviousTitle(null);
  };

  const isBusy = update.isLoading;

  return (
    <div className="flex flex-wrap items-center gap-2 border-l-2 border-sky-400 bg-sky-50/40 px-3 pb-2 text-xs text-gray-700">
      <span className="shrink-0 font-semibold text-gray-600">Wilma Title: </span>
      <span className="min-w-0 flex-1 truncate">{wilmaTitle}</span>
      {titleUpdated && canEdit && (
        <button
          type="button"
          onClick={handleApply}
          disabled={isBusy}
          title="Replace the live eBay title with the Wilma default"
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-sky-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-700 transition hover:bg-sky-50 disabled:opacity-60"
        >
          <ArrowDownTrayIcon className={classNames("h-3.5 w-3.5", update.isLoading && "animate-pulse")} />
          Use Wilma title
        </button>
      )}
      {canEdit && <AiTitleGenerator part={part} onApply={(title) => saveTitle(title, true)} disabled={isBusy} />}
      {previousTitle && canEdit && (
        <button
          type="button"
          onClick={handleUndo}
          disabled={isBusy}
          title={`Revert to previous title: ${previousTitle}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-700 transition hover:bg-amber-50 disabled:opacity-60"
        >
          <ArrowUturnLeftIcon className={classNames("h-3.5 w-3.5", update.isLoading && "animate-pulse")} />
          Undo
        </button>
      )}
      <span
        className={classNames(
          "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
          titleUpdated ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
        )}
        title={
          titleUpdated
            ? "The live eBay title has been customised from the Wilma default"
            : "The live eBay title matches the Wilma default"
        }
      >
        {titleUpdated ? "Updated" : "Default"}
      </span>
    </div>
  );
}
